import { AppBar, Box, Button, Card, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Fade, Grid, IconButton, InputAdornment, InputBase, LinearProgress, List, ListItem, ListItemIcon, ListItemText, MenuItem, Paper, Radio, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Theme, ThemeProvider, Tooltip, Typography, createStyles, makeStyles } from '@material-ui/core';
import { Add, ArrowBack, Delete, Edit, Search } from '@material-ui/icons';
import { Pagination } from '@material-ui/lab';
import { useSnackbar } from 'notistack';
import React from 'react';
import { useHistory, useParams } from 'react-router-dom';
import Page from '../../containers/Page/Page';
import { useLoading } from '../../hooks/loading';
import { api } from '../../services/api';
import { ContextualTheme } from '../../themes';
import { Helpers } from '../../utils/Helpers';

interface IModelo {
    id: string;
    nome: string;
}

interface IModeloData {
    data: IModelo[];
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
}

interface IAtrelamento {
    id: string;
    modelo_id: string;
    modelo_acessorio: string;
    nome_bomba: string;
    grupo: string;
}

interface IParams {
    id: string;
}

function GruposCreate() {

    //Hooks
    const classes = useStyles();
    const history = useHistory();
    const params = useParams<IParams>();

    //Loading
    const { isLoading, startLoading, stopLoading } = useLoading();

    //Alert
    const { enqueueSnackbar: alert } = useSnackbar();

    //States
    const [error, setError] = React.useState<string>('');
    const [bomba, setBomba] = React.useState<IModelo>({} as IModelo);
    const [bombasDialog, setBombasDialog] = React.useState<boolean>(false);
    const [bombasSearch, setBombasSearch] = React.useState<string>('');
    const [bombasData, setBombasData] = React.useState<IModeloData>();
    const [acessorio, setAcessorio] = React.useState<IModelo>({} as IModelo);
    const [acessoriosDialog, setAcessoriosDialog] = React.useState<boolean>(false);
    const [acessoriosSearch, setAcessoriosSearch] = React.useState<string>('');
    const [acessoriosData, setAcessoriosData] = React.useState<IModeloData>();
    const [atrelamentos, setAtrelamentos] = React.useState<IAtrelamento[]>([]);
    const [grupo, setGrupo] = React.useState<string>('');
    const [editDialog, setEditDialog] = React.useState<boolean>(false);
    const [editAtrelamento, setEditAtrelamento] = React.useState<IAtrelamento>({} as IAtrelamento);

    //Effects
    React.useEffect(() => {
        handleFetch();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    React.useEffect(() => {
        handleFetchBombas();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    React.useEffect(() => {
        handleFetchAcessorios();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /**
     * Carregar Atrelamentos.
     * @return void
     */
    async function handleFetch() {
        if (params.id) {
            startLoading('main');
            try {
                const response = await api.get(`grupos/${params.id}`);
                setAtrelamentos(response.data.object.bombas);
                setAcessorio(response.data.object.acessorio);
                setError('');
            } catch (error: any) {
                setError(Helpers.errorFormat(error));
            } finally {
                stopLoading('main');
            }
        }
    }

    /**
     * Atualizar Atrelamentos.
     * @return void
     */
    async function handleFetchUpdate() {
        try {
            const response = await api.get(`grupos/${params.id}`);
            setAtrelamentos(response.data.object.bombas);
            setAcessorio(response.data.object.acessorio);
        } catch (error: any) {
            Helpers.error(error, alert);
        }
    }

    /**
     * Carregar Acessórios.
     * @param page 
     * @return void
     */
    async function handleFetchAcessorios(page: number = 1) {
        startLoading('acessorios');
        try {
            const response = await api.get(`modelos?search=${acessoriosSearch}&page=${page}&tipo=ACESSORIO&ativa=1`);
            setAcessoriosData(response.data.object);
        } catch (error) {
            Helpers.error(error, alert);
        } finally {
            stopLoading('acessorios');
        }
    }

    /**
     * Carregar Bombas.
     * @param page 
     * @return void
     */
    async function handleFetchBombas(page: number = 1) {
        startLoading('bombas');
        try {
            const response = await api.get(`modelos?search=${bombasSearch}&page=${page}&tipo=BOMBA&ativa=1`);
            setBombasData(response.data.object);
        } catch (error) {
            Helpers.error(error, alert);
        } finally {
            stopLoading('bombas');
        }
    }

    /**
     * Salvar Atrelamento.
     * @return void
     */
    async function handleSave() {
        if (acessorio.id && bomba.id && grupo) {
            startLoading('save');
            try {
                const response = await api.post(`grupos/${acessorio.id}`, {
                    modelo_id: bomba.id,
                    grupo: grupo
                });
                alert(response.data.message, { variant: 'success' });
                if (!params.id) { history.push(`/grupos/${response.data.object.modelo_acessorio}`) }
                else { await handleFetchUpdate(); resetForm(); }
            } catch (error) {
                Helpers.error(error, alert);
            } finally {
                stopLoading('save');
            }
        } else {
            alert('Preencha todos os dados para adicionar.', { variant: 'warning' });
        }
    }

    /**
     * Atualizar Atrelamento.
     * @return void
     */
    async function handleUpdate() {
        if (editAtrelamento.grupo) {
            startLoading('update');
            try {
                const response = await api.put(`grupos/${editAtrelamento.id}`, {
                    modelo_id: editAtrelamento.modelo_id,
                    grupo: editAtrelamento.grupo
                });
                await handleFetchUpdate();
                alert(response.data.message, { variant: 'success' });
            } catch (error: any) {
                Helpers.error(error, alert);
            } finally {
                setEditDialog(false);
                setEditAtrelamento({} as IAtrelamento);
                stopLoading('update');
            }
        }
    }

    /**
     * Excluir Atrelamento.
     * @param atrelamento 
     * @return void
     */
    async function handleDelete(atrelamento: IAtrelamento) {
        if (!isLoading(`delete-${atrelamento.id}`)) {
            startLoading(`delete-${atrelamento.id}`);
            try {
                const response = await api.delete(`grupos/${atrelamento.id}`);
                let newData = atrelamentos.filter(i => i.id !== atrelamento.id);
                setAtrelamentos(newData);
                alert(response.data.message, { variant: 'success' });
            } catch (error) {
                Helpers.error(error, alert);
            } finally {
                stopLoading(`delete-${atrelamento.id}`);
            }

        }
    }

    /**
     * Pesquisar Acessórios com Enter.
     * @param event 
     * @return void
     */
    async function handleAcessoriosKeyUp(event: React.KeyboardEvent<HTMLTextAreaElement | HTMLInputElement>) {
        if (event.key === 'Enter') {
            await handleFetchAcessorios();
        }
    }

    /**
    * Pesquisar Bombas com Enter.
    * @param event 
    * @return void
    */
    async function handleBombasKeyUp(event: React.KeyboardEvent<HTMLTextAreaElement | HTMLInputElement>) {
        if (event.key === 'Enter') {
            await handleFetchBombas();
        }
    }

    /**
     * Editar Atrelamento.
     * @param atrelamento
     * @return void
     */
    function handleEdit(atrelamento: IAtrelamento) {
        setEditAtrelamento(atrelamento);
        setEditDialog(true);
    }

    /**
     * Selecionar Acessório.
     * @param modelo 
     * @return void
     */
    function selectAcessorio(modelo: IModelo) {
        setAcessorio(modelo);
        setAcessoriosDialog(false);
    }

    /**
     * Selecionar Bomba.
     * @param modelo 
     * @return void
     */
    function selectBomba(modelo: IModelo) {
        setBomba(modelo);
        setBombasDialog(false);
    }

    /**
     * Limpar formulário.
     * @return void
     */
    function resetForm() {
        setGrupo('');
        setBomba({} as IModelo);
    }

    return (
        <Page error={error} loading={isLoading('main')}>
            <Page.Title>
                <Box display="flex" flexDirection="row" alignItems="center">
                    <IconButton size="small" color="primary" className={classes.backButton} onClick={() => history.push('/grupos')}>
                        <ArrowBack />
                    </IconButton>
                    <Typography variant="h6" component="div">
                        {params.id ? `Editar Atrelamento` : 'Novo Atrelamento'}
                    </Typography>
                </Box>
            </Page.Title>
            <Page.Section>
                <Page.Content>
                    <Box p={2}>
                        <Grid className={classes.row} container spacing={2}>
                            <Grid item xs={12} md={3}>
                                <TextField
                                    id="acessorio_id"
                                    name="acessorio_id"
                                    label="Acessório"
                                    variant="outlined"
                                    disabled={true}
                                    fullWidth={true}
                                    value={acessorio.nome || ''}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <Tooltip title="Selecionar">
                                                    <IconButton color="primary" disabled={!!params.id} edge="end" onClick={() => setAcessoriosDialog(true)}>
                                                        <Search />
                                                    </IconButton>
                                                </Tooltip>
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                                <Dialog open={acessoriosDialog} fullWidth={true} maxWidth="xs">
                                    <AppBar style={{ position: 'relative' }}>
                                        <DialogTitle>Selecionar Acessório</DialogTitle>
                                        <Box mx={3} mb={3}>
                                            <Paper className={classes.custom}>
                                                <InputBase
                                                    autoFocus
                                                    disabled={isLoading('acessorios')}
                                                    className={classes.input}
                                                    placeholder="Pesquisa"
                                                    onChange={(event) => setAcessoriosSearch(event.target.value)}
                                                    onKeyUp={(event) => handleAcessoriosKeyUp(event)}
                                                    value={acessoriosSearch}
                                                />
                                                <IconButton
                                                    disabled={isLoading('acessorios')}
                                                    style={{ padding: 10 }}
                                                    onClick={() => handleFetchAcessorios()}
                                                >
                                                    <Search />
                                                </IconButton>
                                            </Paper>
                                            <Box mt={2}>
                                                <Grid container justifyContent="center">
                                                    <Pagination
                                                        page={acessoriosData?.current_page || 1}
                                                        count={acessoriosData?.last_page || 1}
                                                        onChange={(_event, page) => handleFetchAcessorios(page)}
                                                        color="secondary"
                                                    />
                                                </Grid>
                                            </Box>
                                        </Box>
                                        <Fade in={isLoading('acessorios')} timeout={500}>
                                            <LinearProgress color="secondary"></LinearProgress>
                                        </Fade>
                                    </AppBar>
                                    <DialogContent>
                                        <List>
                                            {acessoriosData && acessoriosData.data && acessoriosData.data.map((modelo, index) => (
                                                <ListItem button key={index} onClick={() => selectAcessorio(modelo)}>
                                                    <ListItemIcon>
                                                        <Radio
                                                            edge="start"
                                                            checked={acessorio.id === modelo.id}
                                                            tabIndex={-1}
                                                            disableRipple={true}
                                                        />
                                                    </ListItemIcon>
                                                    <ListItemText
                                                        primary={modelo.nome}
                                                    />
                                                </ListItem>
                                            ))}
                                        </List>
                                    </DialogContent>
                                    <DialogActions>
                                        <Button color="primary" onClick={() => setAcessoriosDialog(false)}>
                                            Fechar
                                        </Button>
                                    </DialogActions>
                                </Dialog>
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <TextField
                                    id="bomba_id"
                                    name="bomba_id"
                                    label="Bomba"
                                    variant="outlined"
                                    disabled={true}
                                    fullWidth={true}
                                    value={bomba.nome || ''}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <Tooltip title="Selecionar">
                                                    <IconButton color="primary" edge="end" onClick={() => setBombasDialog(true)}>
                                                        <Search />
                                                    </IconButton>
                                                </Tooltip>
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Grid>
                            <Dialog open={bombasDialog} fullWidth={true} maxWidth="xs">
                                <AppBar style={{ position: 'relative' }}>
                                    <DialogTitle>Selecionar Bomba</DialogTitle>
                                    <Box mx={3} mb={3}>
                                        <Paper className={classes.custom}>
                                            <InputBase
                                                autoFocus
                                                disabled={isLoading('bombas')}
                                                className={classes.input}
                                                placeholder="Pesquisa"
                                                onChange={(event) => setBombasSearch(event.target.value)}
                                                onKeyUp={(event) => handleBombasKeyUp(event)}
                                                value={bombasSearch}
                                            />
                                            <IconButton
                                                disabled={isLoading('bombas')}
                                                style={{ padding: 10 }}
                                                onClick={() => handleFetchBombas()}
                                            >
                                                <Search />
                                            </IconButton>
                                        </Paper>
                                        <Box mt={2}>
                                            <Grid container justifyContent="center">
                                                <Pagination
                                                    page={bombasData?.current_page || 1}
                                                    count={bombasData?.last_page || 1}
                                                    onChange={(_event, page) => handleFetchBombas(page)}
                                                    color="secondary"
                                                />
                                            </Grid>
                                        </Box>
                                    </Box>
                                    <Fade in={isLoading('bombas')} timeout={500}>
                                        <LinearProgress color="secondary"></LinearProgress>
                                    </Fade>
                                </AppBar>
                                <DialogContent>
                                    <List>
                                        {bombasData && bombasData.data && bombasData.data.map((modelo, index) => (
                                            <ListItem button key={index} onClick={() => selectBomba(modelo)}>
                                                <ListItemIcon>
                                                    <Radio
                                                        edge="start"
                                                        checked={bomba.id === modelo.id}
                                                        tabIndex={-1}
                                                        disableRipple={true}
                                                    />
                                                </ListItemIcon>
                                                <ListItemText
                                                    primary={modelo.nome}
                                                />
                                            </ListItem>
                                        ))}
                                    </List>
                                </DialogContent>
                                <DialogActions>
                                    <Button color="primary" onClick={() => setBombasDialog(false)}>
                                        Fechar
                                    </Button>
                                </DialogActions>
                            </Dialog>
                            <Grid item xs={12} md={3}>
                                <TextField
                                    id="grupo"
                                    name="grupo"
                                    label="Grupo"
                                    variant="outlined"
                                    select={true}
                                    fullWidth={true}
                                    value={grupo || ''}
                                    onChange={(event) => setGrupo(event.target.value)}
                                >
                                    {Array.from({ length: 20 }).map((_, index) => (
                                        <MenuItem key={index} value={index + 1}>{`GRUPO ${String(index + 1).padStart(2, '0')}`}</MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <Button
                                    style={{ height: '100%' }}
                                    color="secondary"
                                    variant="contained"
                                    size="medium"
                                    fullWidth={true}
                                    disabled={isLoading('save')}
                                    startIcon={isLoading('save') ? <CircularProgress color="inherit" size="15px" /> : <Add />}
                                    onClick={() => handleSave()}>
                                    Adicionar
                                </Button>
                            </Grid>
                        </Grid>
                        {atrelamentos &&
                            <Card variant="outlined">
                                <TableContainer>
                                    <Table stickyHeader>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Bomba</TableCell>
                                                <TableCell>Grupo</TableCell>
                                                <TableCell align="center">Ações</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {atrelamentos.map((atrelamento) => (
                                                <TableRow
                                                    key={atrelamento.id}
                                                    hover={true}
                                                >
                                                    <TableCell>{atrelamento.nome_bomba}</TableCell>
                                                    <TableCell>{String(atrelamento.grupo).padStart(2, '0')}</TableCell>
                                                    <TableCell align="center">
                                                        <Box display="flex" flexDirection="row" justifyContent="center" alignItems="center">
                                                            <Box mr={2}>
                                                                <IconButton size="small" color="primary" onClick={() => handleEdit(atrelamento)}>
                                                                    <Edit />
                                                                </IconButton>
                                                            </Box>
                                                            <ThemeProvider theme={ContextualTheme}>
                                                                {isLoading(`delete-${atrelamento.id}`) ?
                                                                    <CircularProgress color="secondary" size={16} />
                                                                    :
                                                                    <IconButton size="small" color="secondary" onClick={() => handleDelete(atrelamento)}>
                                                                        <Delete />
                                                                    </IconButton>
                                                                }
                                                            </ThemeProvider>
                                                        </Box>
                                                        <Dialog open={editDialog} fullWidth={true} maxWidth="xs">
                                                            <AppBar style={{ position: 'relative' }}>
                                                                <DialogTitle>Editar Atrelamento</DialogTitle>
                                                            </AppBar>
                                                            <DialogContent>
                                                                <Box p={2}>
                                                                    <Grid container spacing={2}>
                                                                        <Grid item xs={12} md={12}>
                                                                            <TextField
                                                                                id="edit_bomba"
                                                                                name="edit_bomba"
                                                                                label="Bomba"
                                                                                variant="outlined"
                                                                                disabled={true}
                                                                                fullWidth={true}
                                                                                value={editAtrelamento.nome_bomba || ''}
                                                                            />
                                                                        </Grid>
                                                                        <Grid item xs={12} md={12}>
                                                                            <TextField
                                                                                id="edit_grupo"
                                                                                name="edit_grupo"
                                                                                label="Grupo"
                                                                                variant="outlined"
                                                                                select={true}
                                                                                fullWidth={true}
                                                                                value={editAtrelamento.grupo || ''}
                                                                                onChange={(event) => setEditAtrelamento({ ...editAtrelamento, grupo: event.target.value })}
                                                                            >
                                                                                {Array.from({ length: 20 }).map((_, index) => (
                                                                                    <MenuItem key={index} value={index + 1}>{`GRUPO ${String(index + 1).padStart(2, '0')}`}</MenuItem>
                                                                                ))}
                                                                            </TextField>
                                                                        </Grid>
                                                                    </Grid>
                                                                </Box>
                                                            </DialogContent>
                                                            <DialogActions>
                                                                <Button color="primary" onClick={() => setEditDialog(false)}>
                                                                    Fechar
                                                                </Button>
                                                                <Button
                                                                    color="primary"
                                                                    variant="contained"
                                                                    disabled={isLoading('update')}
                                                                    startIcon={isLoading('update') ? <CircularProgress color="inherit" size={16} /> : undefined}
                                                                    onClick={() => handleUpdate()}>
                                                                    Salvar
                                                                </Button>
                                                            </DialogActions>
                                                        </Dialog>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Card>
                        }
                    </Box>
                </Page.Content>
            </Page.Section>
        </Page>
    );
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        backButton: {
            marginRight: theme.spacing(1),
        },
        row: {
            marginBottom: theme.spacing(1)
        },
        custom: {
            padding: '2px 4px',
            display: 'flex',
            alignItems: 'center',
        },
        input: {
            marginLeft: theme.spacing(1),
            flex: 1,
        },
    })
);

export default GruposCreate;